import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { EventTaskTableEditor } from './EventTaskTableEditor';
import { useAddUserBotTasksInline, useParseUserTasks } from '@/lib/sdk/adhoc/queries';
import { UserBotApi } from '@/lib/sdk/adhoc/api';
import { useToast } from '@/client/hooks/useToast';
import type { UserBot, UserBotTaskInput, BulkTaskError } from '@/lib/sdk/adhoc/types';
import type { TeamMember } from '@/lib/sdk/team/types';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface EventTaskDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  event: UserBot;
  teamMembers: TeamMember[];
  onSaved?: () => void;
}

export function EventTaskDrawer({ isOpen, onClose, orgId, event, teamMembers, onSaved }: EventTaskDrawerProps) {
  const toast = useToast();
  const addUserBotTasksInline = useAddUserBotTasksInline();
  const parseUserTasks = useParseUserTasks();
  const [rowErrors, setRowErrors] = useState<BulkTaskError[]>([]);
  const [importErrors, setImportErrors] = useState<BulkTaskError[]>([]);
  const [editorErrors, setEditorErrors] = useState(0);
  const [importedRows, setImportedRows] = useState<UserBotTaskInput[] | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      setRowErrors([]);
      setImportErrors([]);
      setEditorErrors(0);
      setImportedRows(undefined);
    }
  }, [isOpen]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await UserBotApi.getUserBotTemplate(orgId);
      triggerDownload(blob, 'event_tasks_template.xlsx');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleImport = (file: File) => {
    setImportErrors([]);
    parseUserTasks.mutate({ orgId, file }, {
      onSuccess: (result) => {
        if (result.errors && result.errors.length > 0) {
          setImportErrors(result.errors);
          return;
        }
        setImportedRows(result.tasks ?? []);
      },
      onError: () => {
        toast.error('Failed to parse Excel file');
      },
    });
  };

  const handleValidRows = useCallback((rows: UserBotTaskInput[]) => {
    setEditorErrors(0);
    if (rows.length === 0) {
      toast.error('Add at least one task before submitting');
      return;
    }
    addUserBotTasksInline.mutate(
      { orgId, botCode: event.short_code!, data: { tasks: rows } },
      {
        onSuccess: (result) => {
          if (result.errors && result.errors.length > 0) {
            setRowErrors(result.errors);
            return;
          }
          if (result.bot) {
            toast.success('Tasks added successfully');
            onSaved?.();
            onClose();
          }
        },
      }
    );
  }, [orgId, event.short_code, addUserBotTasksInline, onSaved, onClose, toast]);

  const handleValidationError = useCallback((count: number) => {
    setEditorErrors(count);
  }, []);

  const handleSubmit = () => {
    setRowErrors([]);
    document.getElementById('event-task-drawer-validate')?.click();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="full">
      <DrawerOverlay />
      <DrawerContent maxH="80vh" bg="bg-surface" data-testid="ev-task-drawer">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="border-default" py={3}>
          <Text fontWeight="semibold">Add Tasks to {event.title ?? 'Event'}</Text>
        </DrawerHeader>

        <DrawerBody p={4} overflow="auto">
          <Box maxW="1200px" mx="auto">
            <EventTaskTableEditor
              teamMembers={teamMembers}
              onValidRows={handleValidRows}
              onValidationError={handleValidationError}
              validateTriggerId="event-task-drawer-validate"
              importedRows={importedRows}
              onDownloadTemplate={handleDownloadTemplate}
              onImport={handleImport}
              isImporting={parseUserTasks.isPending}
            />

            {editorErrors > 0 && (
              <Text fontSize="sm" color="red.500" mt={2}>
                {editorErrors} row{editorErrors !== 1 ? 's' : ''} have errors — fix before submitting.
              </Text>
            )}

            {importErrors.length > 0 && (
              <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3} mt={3}>
                <Text fontWeight="medium" color="red.700" mb={2} fontSize="sm">
                  Import errors ({importErrors.length}):
                </Text>
                <VStack align="stretch" spacing={1} maxH="120px" overflowY="auto">
                  {importErrors.map((err) => (
                    <Text key={err.row_number} fontSize="sm" color="red.600">
                      Row {err.row_number}: {err.message}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}

            {rowErrors.length > 0 && (
              <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3} mt={3}>
                <Text fontWeight="medium" color="red.700" mb={2} fontSize="sm">
                  Errors ({rowErrors.length}):
                </Text>
                <VStack align="stretch" spacing={1} maxH="120px" overflowY="auto">
                  {rowErrors.map((err) => (
                    <Text key={err.row_number} fontSize="sm" color="red.600">
                      Row {err.row_number}: {err.message}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}
          </Box>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="border-default" py={3}>
          <HStack spacing={3} justify="flex-end" w="full">
            <Button variant="ghost" onClick={onClose} isDisabled={addUserBotTasksInline.isPending} data-testid="ev-task-cancel-btn">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={addUserBotTasksInline.isPending}
              loadingText="Adding..."
              data-testid="ev-task-submit-btn"
            >
              Add Tasks
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
